// NOTE: Must be imported first
import 'focus-visible/dist/focus-visible.js';

import { Global } from '@emotion/react';
import { Fragment, memo } from 'react';

import { ThemeProvider, ThemeProviderProps, useTheme } from '@reckon-web/theme';

import { resetStyles } from './reset-styles';
import {
  DefaultLinkComponent,
  LinkComponent,
  LinkComponentContext,
} from './linkComponent';

// Core
// ------------------------------

type CoreProps = ThemeProviderProps & {
  linkComponent?: LinkComponent;
};

export const Core = ({
  children,
  linkComponent = DefaultLinkComponent,
  paletteResolver,
  theme,
}: CoreProps) => {
  return (
    <LinkComponentContext.Provider value={linkComponent}>
      <ThemeProvider paletteResolver={paletteResolver} theme={theme}>
        <GlobalStyles />
        <App>{children}</App>
      </ThemeProvider>
    </LinkComponentContext.Provider>
  );
};

// NOTE: only re-render leaf nodes that listen to theme changes

const App = memo((props) => <Fragment {...props} />);
App.displayName = 'App';

// Global styles
// ------------------------------

const GlobalStyles = () => {
  const { palette, typography } = useTheme();

  return (
    <Fragment>
      <Global styles={resetStyles} />

      {/* focus ring */}
      <Global
        styles={{
          '*:focus': {
            outline: 0,
          },
          '[data-focus-visible-added]': {
            outline: `2px solid ${palette.global.focusRing}`,
            outlineOffset: 2,
          },

          // remove firefox dotted focus-ring
          '& [type="button"]:-moz-focusring, [type="reset"]:-moz-focusring, [type="submit"]:-moz-focusring, button:-moz-focusring':
            {
              outline: 'none !important',
            },
        }}
      />

      {/* defaults */}
      <Global
        styles={{
          body: {
            backgroundColor: palette.background.base,
            fontFamily: typography.fontFamily.body, // TODO: remove

            // optimize legibility
            fontFeatureSettings: '"liga" 1', // Enable OpenType ligatures in IE
            textRendering: 'optimizeLegibility',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            MozFontFeatureSettings: '"liga" on',
          },

          // TODO: global "selection" styles?
        }}
      />
    </Fragment>
  );
};
